// Network Devices (RADIUS mode): /pam/radius — the VPNs, switches, firewalls and Wi-Fi
// controllers that send logins to the tenant's FreeRADIUS servers.
//
// The list is PAM's /network_device/ListDevices, plus /radius/DeviceActivity for each device's
// status, last login and the server it sends logins to (resolved to a name with ListServers).
// Devices register themselves on their first login, so adding one here is optional: it names a
// device before that, so a policy can pick it. Policies can also target a raw IP, CIDR or
// NAS-Identifier. The device itself is still added to clients.conf on the FreeRADIUS server.
//
// OLD (before the console design system): an MUI DataGrid with a Cascader filter, an
// AutoComplete search, a Joyride tour (commented out), "Connect FreeRADIUS" + "Add Radius
// Device" buttons, a hard-coded "Active" Status badge (radius-hardening placeholder, now real
// from DeviceActivity), and Delete straight from the ⋮ menu with no confirmation.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Table, Tooltip, message } from "antd";
import { Plus, RefreshCw, Search } from "lucide-react";
import axios from "axios";

import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_PAM_API } from "../../constants";
import { logOutUser } from "../../common";
import { listADPolicies } from "../../service/adPolicyService";
import { listRadiusServers, radiusDeviceActivity } from "../../service/radiusService";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  StatusPill,
  TableCard,
  anTableProps,
  fmtDate,
} from "../../design-system/tableUi";
import { AN_COLORS } from "../../design-system/tokens";
import RadiusDeviceDrawer from "./RadiusDeviceDrawer";
import DevicePoliciesDrawer from "./DevicePoliciesDrawer";
import {
  DEVICE_STATUS,
  DEVICE_TYPES,
  countPoliciesNamingDevice,
  deviceDisplayName,
  deviceStatusView,
  deviceTypeText,
} from "./radiusDeviceModel";

const headers = () => ({ withCredentials: true, "X-Authorization": AUTH_TOKEN() });

const TYPE_FILTERS = [...DEVICE_TYPES, "Not set"].map((t) => ({ value: t, label: t }));
const STATUS_FILTERS = Object.entries(DEVICE_STATUS).map(([value, v]) => ({ value, label: v.label }));

const ListRadiusDevices = () => {
  const [notification, notificationCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // id → DeviceActivity row ({ radiusServerId, lastLoginMs, discovered, status }).
  const [activity, setActivity] = useState({});
  const [servers, setServers] = useState([]);
  // RADIUS policies, to count and show which apply to each device.
  const [policies, setPolicies] = useState([]);

  const [search, setSearch] = useState("");
  const [type, setType] = useState(undefined);
  const [status, setStatus] = useState(undefined);

  // Add / edit panel: device null = add.
  const [deviceDrawer, setDeviceDrawer] = useState({ open: false, device: null });
  const [policiesDrawer, setPoliciesDrawer] = useState({ open: false, device: null });

  // ponytail: loads up to 200 devices and searches / pages them in the browser. A tenant
  // with more devices than that needs the list API to search by name.
  const fetchDevices = useCallback(() => {
    setLoading(true);
    axios
      .post(
        `${REACT_APP_PAM_API}/network_device/ListDevices`,
        {
          domainId: DOMAIN_ID(),
          pageId: 1,
          pageSize: 200,
          search: "",
          filter: { filterBy: "", value: "" },
          token: AUTH_TOKEN(),
          orgId: ORG_ID(),
          MachineType: "radius",
          tenantId: DOMAIN_ID(),
        },
        { headers: headers() },
      )
      .then((res) => {
        setDevices(res?.data?.network_devices || []);
        setLoadError("");
      })
      .catch((err) => {
        if (err?.response?.data?.message === "Invalid credentials" || err?.response?.status === 401) {
          notification.error("Credentials are invalid");
          logOutUser();
          return;
        }
        setLoadError("Could not load the network devices.");
        notification.error("Could not load the network devices.");
      })
      .finally(() => setLoading(false));

    // Status, last login and server: the list still shows without them.
    radiusDeviceActivity({
      onSuccess: (rows) => setActivity(Object.fromEntries((Array.isArray(rows) ? rows : []).map((r) => [r.id, r]))),
      onFailure: () => setActivity({}),
    });
  }, [notification]);

  const fetchServers = useCallback(() => {
    listRadiusServers({
      onSuccess: (rows) => setServers(Array.isArray(rows) ? rows : []),
      onFailure: () => setServers([]),
    });
  }, []);

  // RADIUS policies, for the Policies column and the device Policies panel.
  const fetchPolicies = useCallback(() => {
    listADPolicies({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyType: "radius", filters: [{ filterParameter: "policyType", filterValue: "radius" }], page: 1, limit: 200 },
      onSuccess: (res) => {
        // Active and paused only: pending or revoked policies are not enforced.
        const list = (res?.policies || res?.data || []).filter(
          (p) => (p?.policyType || "").toLowerCase() === "radius" && ["Approved", "Suspended"].includes(p?.status),
        );
        setPolicies(list);
      },
      // Devices still list without policies; the Policies column just shows 0.
      onFailure: () => setPolicies([]),
    });
  }, []);

  useEffect(() => {
    fetchDevices();
    fetchServers();
    fetchPolicies();
  }, [fetchDevices, fetchServers, fetchPolicies]);

  const serverName = useMemo(() => Object.fromEntries(servers.map((s) => [s.id, s.name])), [servers]);

  const q = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      devices.filter((d) => {
        const a = activity[d.id];
        if (type && deviceTypeText(d) !== type) return false;
        if (status && a?.status !== status) return false;
        if (q) {
          const hay = [deviceDisplayName(d), d.ip_address, d.nas_identifier, d.client_ip, serverName[a?.radiusServerId]]
            .filter(Boolean)
            .map((s) => String(s).toLowerCase());
          if (!hay.some((s) => s.includes(q))) return false;
        }
        return true;
      }),
    [devices, activity, serverName, type, status, q],
  );

  // ─── Delete ──────────────────────────────────────────────────────────────────
  const confirmDelete = (d) =>
    modal.confirm({
      title: `Delete ${deviceDisplayName(d) || "this device"}?`,
      content:
        "Policies that name it stop matching it by name. If it keeps sending logins it registers again as Discovered; remove it from clients.conf to stop that.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        axios
          .post(`${REACT_APP_PAM_API}/network_device/DeleteDevice`, { client_ip: d?.client_ip, org_id: ORG_ID() }, { headers: headers() })
          .then(() => {
            notification.success(`${deviceDisplayName(d) || "Device"} deleted.`);
            fetchDevices();
            fetchServers();
          })
          .catch(() => notification.error("Could not delete the device.")),
    });

  const refreshAfterSave = () => {
    fetchDevices();
    // Devices per server changes when a device moves.
    fetchServers();
  };

  // Menu clicks are rendered in a portal but still bubble through React to the row's onClick.
  const menuItem = (key, label, onClick, extra = {}) => ({
    key,
    label,
    onClick: ({ domEvent }) => {
      domEvent?.stopPropagation();
      onClick();
    },
    ...extra,
  });

  // OLD columns: Name · NAS IP · NAS-Identifier · Type · Policies · Status (hard-coded Active) · Actions.
  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, d) => (
        <span style={{ fontWeight: 500, color: AN_COLORS.ink, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {deviceDisplayName(d) || "—"}
        </span>
      ),
      sorter: (a, b) => deviceDisplayName(a).localeCompare(deviceDisplayName(b)),
    },
    {
      title: "NAS IP",
      dataIndex: "ip_address",
      width: "9rem",
      render: (ip, d) => <MonoCell>{ip || d.client_ip}</MonoCell>,
    },
    {
      title: "NAS-Identifier",
      dataIndex: "nas_identifier",
      width: "9rem",
      render: (v) => <MonoCell>{v}</MonoCell>,
    },
    {
      title: "Type",
      key: "type",
      width: "6.5rem",
      render: (_, d) => {
        const t = deviceTypeText(d);
        return <span style={{ color: t === "Not set" ? AN_COLORS.muted : AN_COLORS.ink2 }}>{t}</span>;
      },
    },
    {
      title: "Sends logins to",
      key: "server",
      width: "9rem",
      render: (_, d) => {
        const id = activity[d.id]?.radiusServerId;
        if (!id) return <span style={{ color: AN_COLORS.muted }}>—</span>;
        return (
          <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {serverName[id] || <span style={{ color: AN_COLORS.muted }}>Unknown server</span>}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: "7.5rem",
      render: (_, d) => {
        const v = deviceStatusView(activity[d.id]);
        return v ? <StatusPill tone={v.tone} label={v.label} /> : <span style={{ color: AN_COLORS.muted }}>—</span>;
      },
    },
    {
      title: "Last login",
      key: "lastLogin",
      width: "11rem",
      render: (_, d) => {
        const ms = activity[d.id]?.lastLoginMs;
        return ms ? <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(ms)}</span> : <span style={{ color: AN_COLORS.muted }}>Never</span>;
      },
      sorter: (a, b) => (activity[a.id]?.lastLoginMs || 0) - (activity[b.id]?.lastLoginMs || 0),
    },
    {
      title: "Policies",
      key: "policies",
      width: "5.5rem",
      render: (_, d) => (
        <Tooltip title="Policies that name this device">
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{countPoliciesNamingDevice(policies, d)}</span>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "actions",
      width: "3.5rem",
      align: "right",
      render: (_, d) => (
        <RowActionMenu
          ariaLabel={`Actions for ${deviceDisplayName(d)}`}
          items={[
            menuItem("edit", "Edit", () => setDeviceDrawer({ open: true, device: d })),
            menuItem("policies", "Policies", () => setPoliciesDrawer({ open: true, device: d })),
            { type: "divider" },
            menuItem("delete", "Delete…", () => confirmDelete(d), { danger: true }),
          ]}
        />
      ),
    },
  ];

  const applied = [
    q && { key: "search", label: `Search: ${search.trim()}`, clear: () => setSearch("") },
    type && { key: "type", label: `Type: ${type}`, clear: () => setType(undefined) },
    status && { key: "status", label: `Status: ${DEVICE_STATUS[status]?.label}`, clear: () => setStatus(undefined) },
  ].filter(Boolean);

  // OLD: <Button type="primary" id="addRadiusBtn">Add Radius Device</Button> beside a "Connect FreeRADIUS" link.
  const addButton = (
    <Button icon={<Plus size={13} strokeWidth={2.2} />} onClick={() => setDeviceDrawer({ open: true, device: null })}>
      Add device
    </Button>
  );

  let emptyText;
  if (loading) emptyText = "Loading…";
  else if (loadError)
    emptyText = (
      <div style={{ padding: "1.75rem 0.75rem", display: "grid", gap: "0.75rem", justifyItems: "center" }}>
        <span style={{ fontSize: "0.8rem", color: AN_COLORS.muted }}>{loadError}</span>
        <Button icon={<RefreshCw size={13} />} onClick={fetchDevices}>
          Try again
        </Button>
      </div>
    );
  else if (devices.length === 0)
    emptyText = (
      <div style={{ padding: "1.75rem 0.75rem", display: "grid", gap: "0.75rem", justifyItems: "center" }}>
        <span style={{ fontSize: "0.8rem", color: AN_COLORS.muted, maxWidth: "32rem" }}>
          No devices yet. Devices appear here after their first login through your RADIUS server. You can also add one now
          to use it in a policy.
        </span>
        {addButton}
      </div>
    );
  else emptyText = "No devices match your filters.";

  return (
    <PageShell>
      {notificationCtx}
      {modalCtx}
      <PageHeader
        eyebrow="RADIUS"
        // OLD: <h2 className="title">Radius Devices</h2>
        title="Network Devices"
        subtitle="Devices that send logins to your RADIUS servers. They register themselves on their first login."
        chips={devices.length > 0 && <CountChip count={devices.length} label={devices.length === 1 ? "Device" : "Devices"} />}
        actions={addButton}
      />

      <FilterCard
        columns="repeat(auto-fit, minmax(11rem, 1fr))"
        applied={applied}
        onClearAll={() => {
          setSearch("");
          setType(undefined);
          setStatus(undefined);
        }}
        showing={visible.length}
        total={devices.length}
      >
        <FilterField label="Search">
          <Input
            allowClear
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            placeholder="Name, IP, NAS-Identifier or server"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ height: 36 }}
          />
        </FilterField>
        <FilterField label="Type">
          <Select allowClear placeholder="Any" value={type} onChange={setType} options={TYPE_FILTERS} style={{ width: "100%", height: 36 }} />
        </FilterField>
        <FilterField label="Status">
          <Select allowClear placeholder="Any" value={status} onChange={setStatus} options={STATUS_FILTERS} style={{ width: "100%", height: 36 }} />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={visible}
          rowKey="id"
          loading={loading && devices.length > 0}
          tableLayout="fixed"
          scroll={{ x: "70rem" }}
          locale={{ emptyText }}
          pagination={visible.length > 10 ? { pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 25, 50] } : false}
          onRow={(d) => ({
            onClick: () => setDeviceDrawer({ open: true, device: d }),
            style: { cursor: "pointer" },
          })}
        />
      </TableCard>

      <RadiusDeviceDrawer
        open={deviceDrawer.open}
        device={deviceDrawer.device}
        activity={deviceDrawer.device ? activity[deviceDrawer.device.id] : null}
        servers={servers}
        onClose={() => setDeviceDrawer({ open: false, device: null })}
        onSaved={refreshAfterSave}
      />
      <DevicePoliciesDrawer
        open={policiesDrawer.open}
        device={policiesDrawer.device}
        policies={policies}
        onClose={() => setPoliciesDrawer({ open: false, device: null })}
      />
    </PageShell>
  );
};

export default ListRadiusDevices;
